<script setup lang="ts">
import type { SizeChart } from '~~/shared/types/store'
import { collectMeasurements, findSizeColumn } from '~~/shared/utils/sizeChartLookup'

const props = defineProps<{
  sizeChart: SizeChart | null
  selectedSize: string
}>()

const measurements = computed(() => {
  if (!props.sizeChart || !props.selectedSize) return []
  if (props.sizeChart.headers.length === 0) return []
  const lookup = findSizeColumn(
    props.sizeChart.headers,
    props.sizeChart.rows,
    props.selectedSize,
  )
  if (!lookup) return []
  return collectMeasurements(props.sizeChart.rows, lookup)
})
</script>

<template>
  <Transition name="size-measurements">
    <div v-if="measurements.length" class="size-measurements" :aria-label="`Pomiary dla rozmiaru ${selectedSize}`">
      <p class="size-measurements__title">
        <svg
          class="size-measurements__icon"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M3 10l4 4 4-4 4 4 4-4 4 4" />
          <path d="M5 8v4M9 8v4M13 8v4M17 8v4M21 8v4" />
        </svg>
        <span>Pomiary dla rozmiaru <strong>{{ selectedSize }}</strong></span>
      </p>
      <dl class="size-measurements__list">
        <div
          v-for="entry in measurements"
          :key="entry.label"
          class="size-measurements__item"
        >
          <dt>{{ entry.label }}</dt>
          <dd>{{ entry.value }}</dd>
        </div>
      </dl>
    </div>
  </Transition>
</template>

<style scoped>
.size-measurements {
  margin: 14px 0 0;
  padding: 12px 16px;
  background: var(--color-surface, #f2f2f2);
  border-radius: var(--radius-md, 4px);
  border: 1px solid transparent;
  transition: border-color var(--transition-fast, 150ms ease);
}

.size-measurements__title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 8px;
  font-size: 0.8rem;
  letter-spacing: var(--tracking-wide, 0.05em);
  text-transform: uppercase;
  color: var(--color-text-secondary, #5a5a5a);
  font-weight: var(--font-medium, 500);
}

.size-measurements__title strong {
  color: var(--color-text-primary, #151515);
  font-weight: var(--font-semibold, 600);
  letter-spacing: var(--tracking-wider, 0.1em);
}

.size-measurements__icon {
  color: var(--color-text-muted, #9a9a9a);
  flex-shrink: 0;
}

.size-measurements__list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 18px;
  margin: 0;
  padding: 0;
  font-size: 0.85rem;
}

.size-measurements__item {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  margin: 0;
}

.size-measurements__item dt {
  margin: 0;
  color: var(--color-text-secondary, #5a5a5a);
  font-weight: var(--font-normal, 400);
}

.size-measurements__item dt::after {
  content: ':';
  color: var(--color-text-muted, #9a9a9a);
}

.size-measurements__item dd {
  margin: 0;
  color: var(--color-text-primary, #151515);
  font-weight: var(--font-medium, 500);
  font-variant-numeric: tabular-nums;
}

.size-measurements-enter-active,
.size-measurements-leave-active {
  transition:
    opacity var(--transition-fast, 150ms ease),
    transform var(--transition-fast, 150ms ease);
  overflow: hidden;
}

.size-measurements-enter-from,
.size-measurements-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
